import { Component, OnInit, OnDestroy } from "@angular/core";
import { trigger, transition, style, animate } from "@angular/animations";
import {
  NotificationService,
  NotificationDto,
} from "../../services/notification.service";
import { Subscription } from "rxjs";
import { Router } from "@angular/router";
import { DrawerService } from "../drawer/drawer.service";
import { NotificationsDrawerComponent } from "../notifications/notifications.component";
import { AlertService } from "../../services/alert.service";

@Component({
  selector: "app-notification-dropdown",
  templateUrl: "./notification-dropdown.component.html",
  styleUrls: ["./notification-dropdown.component.scss"],
  animations: [
    trigger("slideDown", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(-10px)" }),
        animate(
          "200ms ease-out",
          style({ opacity: 1, transform: "translateY(0)" }),
        ),
      ]),
      transition(":leave", [
        animate(
          "200ms ease-in",
          style({ opacity: 0, transform: "translateY(-10px)" }),
        ),
      ]),
    ]),
  ],
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  itemsNotification: NotificationDto[] = [];
  total: number = 0;
  isOpen: boolean = false;
  isLoading: boolean = false;

  // Pagination & Scroll
  currentPage: number = 1;
  pageSize: number = 10;
  hasMore: boolean = true;
  scrollDistance = 1;
  throttle = 100;

  private subscriptions: Subscription[] = [];

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private drawerService: DrawerService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    // Subscribe to unread count changes
    this.subscriptions.push(
      this.notificationService.unreadCount$.subscribe((count) => {
        this.total = count;
      }),
    );

    // Subscribe to notifications changes
    this.subscriptions.push(
      this.notificationService.notifications$.subscribe((notifications) => {
        this.itemsNotification = notifications;
      }),
    );

    // Load initial data
    this.loadUnreadCount();

    // Polling is no longer needed - using SignalR for real-time updates
    // this.notificationService.startPolling(30000);
  }

  ngOnDestroy(): void {
    // Cleanup subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Stop polling
    this.notificationService.stopPolling();
  }

  /**
   * Toggle dropdown
   */
  toggleDropdown(): void {
    this.isOpen = !this.isOpen;

    if (this.isOpen && this.itemsNotification.length === 0) {
      this.loadNotifications();
    }
  }

  /**
   * Load unread count only (light request)
   */
  loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe();
  }

  /**
   * Load danh sách thông báo
   */
  loadNotifications(): void {
    this.isLoading = true;
    this.notificationService
      .getNotifications(this.currentPage, this.pageSize)
      .subscribe({
        next: (response) => {
          // Data is handled by the subscription in ngOnInit via the service subjects
          const items = response.notifications || response.Notifications || [];
          this.hasMore = items.length >= this.pageSize;
          this.isLoading = false;
        },
        error: (err) => {
          console.error("Error loading notifications:", err);
          this.isLoading = false;
        },
      });
  }

  /**
   * Load more notifications (infinite scroll)
   */
  loadMore(): void {
    if (!this.hasMore || this.isLoading) return;

    this.currentPage++;
    this.isLoading = true;

    this.notificationService
      .getNotifications(this.currentPage, this.pageSize)
      .subscribe({
        next: (response) => {
          // Data is handled by the subscription in ngOnInit via the service subjects
          const items = response.notifications || response.Notifications || [];
          this.hasMore = items.length >= this.pageSize;
          this.isLoading = false;
        },
        error: (err) => {
          console.error("Error loading more:", err);
          this.isLoading = false;
        },
      });
  }

  /**
   * Đánh dấu đã đọc khi click vào notification
   */
  markAsRead(notification: NotificationDto, event?: Event, navigate: boolean = false): void {
    if (event) {
      event.stopPropagation();
    }

    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.id).subscribe(() => {
        notification.isRead = true;
        notification.daDoc = true;
        notification.readAt = new Date();

        if (navigate) {
          this.navigateToDetail(notification);
        }
      });
    } else if (navigate) {
      this.navigateToDetail(notification);
    }
  }

  /**
   * Xử lý click vào nút xem chi tiết
   */
  viewDetail(notification: NotificationDto, event: Event): void {
    this.markAsRead(notification, event, true);
  }

  /**
   * Xử lý navigate tới trang chi tiết
   */
  navigateToDetail(notification: NotificationDto): void {
    // Navigate nếu là Invoice/Order
    if (notification.entityType === "OrderCustomer" && notification.entityId) {
      this.router.navigate(["/invoice"], {
        queryParams: { invoiceId: notification.entityId },
      });
    }
    // Parse metadata để lấy URL (fallback)
    else if (notification.metaData) {
      try {
        const metadata = JSON.parse(notification.metaData);
        if (metadata.url) {
          this.router.navigate([metadata.url]);
        }
      } catch (e) {
        console.error("Invalid metadata JSON:", e);
      }
    }

    // Đóng dropdown sau khi click
    this.isOpen = false;
  }

  /**
   * Đánh dấu tất cả là đã đọc
   */
  markAllAsRead(): void {
    this.notificationService.markAsRead().subscribe(() => {
      this.itemsNotification.forEach((n) => {
        n.isRead = true;
        n.daDoc = true;
        n.readAt = new Date();
      });
      this.total = 0;
    });
  }

  /**
   * Xóa thông báo
   */
  deleteNotification(notification: NotificationDto, event: Event): void {
    event.stopPropagation();

    this.alertService.confirm("Bạn có chắc muốn xóa thông báo này?", "Xóa thông báo").subscribe(ok => {
      if (ok) {
        this.notificationService
          .deleteNotification(notification.id)
          .subscribe(() => {
            this.itemsNotification = this.itemsNotification.filter(
              (n) => n.id !== notification.id,
            );

            // Cập nhật count nếu notification chưa đọc
            if (!notification.isRead && this.total > 0) {
              this.total--;
            }
          });
      }
    });
  }

  getClass(item: NotificationDto): string {
    if (item.tieuDe?.toLowerCase().includes("hóa đơn")) {
      return "txt-success";
    }
    return "";
  }

  /**
   * Xóa tất cả thông báo đã hiển thị
   */
  deleteAllNotifications(): void {
    if (this.itemsNotification.length === 0) return;

    this.alertService
      .confirm(
        `Bạn có chắc muốn xóa ${this.itemsNotification.length} thông báo đã tải?`,
        "Xóa thông báo"
      )
      .subscribe((ok) => {
        if (ok) {
          const ids = this.itemsNotification.map((n) => n.id);
          this.notificationService.deleteBulk(ids).subscribe(() => {
            if (this.total > 0) {
              this.loadUnreadCount();
            }
          });
        }
      });
  }

  /**
   * Mở drawer thông báo
   */
  openNotificationDrawer(): void {
    this.isOpen = false;
    this.drawerService.open(NotificationsDrawerComponent, {
      title: "Thông báo",
      width: "450px",
    });
  }

  /**
   * Get CSS class cho notification item
   */
  getNotificationClass(notification: NotificationDto): string {
    const baseClass = "notification-item";
    const unreadClass = !notification.isRead ? "unread" : "";
    const typeClass = `type-${notification.notificationType.toLowerCase()}`;

    return `${baseClass} ${unreadClass} ${typeClass}`.trim();
  }

  /**
   * Close dropdown when click outside
   */
  onClickOutside(): void {
    this.isOpen = false;
  }
}
